import React from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { usePatternStudio } from '../context/PatternStudioContext';

const CATEGORIES = [
  { value: 'yarn',       label: '🧶 Yarn',        placeholder: 'e.g. Paintbox Simply DK – Cream' },
  { value: 'hook',       label: '🪝 Hook',         placeholder: 'e.g. 3.5mm Crochet Hook' },
  { value: 'safety_eye', label: '👁 Safety Eyes',  placeholder: 'e.g. Black Safety Eyes 12mm' },
  { value: 'stuffing',   label: '☁️ Stuffing',     placeholder: 'e.g. Polyfill Stuffing' },
  { value: 'notion',     label: '🧵 Other',        placeholder: 'e.g. Yarn Needle, Stitch Marker' },
];

const MATERIAL_TEMPLATES = [
  { category: 'yarn',       name: 'Worsted weight yarn – main color', amount: '100g' },
  { category: 'hook',       name: '4mm Crochet Hook',                amount: '1 piece' },
  { category: 'safety_eye', name: 'Black Safety Eyes 12mm',          amount: '2 pcs' },
  { category: 'stuffing',   name: 'Polyfill Stuffing',               amount: 'as needed' },
  { category: 'notion',     name: 'Yarn Needle',                     amount: '1' },
];

function SortableRow({ material, onUpdate, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: material.id });
  const catInfo = CATEGORIES.find(c => c.value === material.category) || CATEGORIES[0];

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex items-center gap-2 p-3 bg-white rounded-xl border border-gray-200 group"
    >
      <div {...attributes} {...listeners} className="cursor-grab text-gray-300 hover:text-gray-500 flex-shrink-0">
        <GripVertical size={16} />
      </div>

      <select
        value={material.category}
        onChange={e => onUpdate(material.id, { category: e.target.value })}
        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:border-stone-400 flex-shrink-0"
        style={{ width: '120px' }}
      >
        {CATEGORIES.map(c => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>

      <input
        type="text"
        value={material.name}
        onChange={e => onUpdate(material.id, { name: e.target.value })}
        placeholder={catInfo.placeholder}
        className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-stone-400 min-w-0"
      />

      <input
        type="text"
        value={material.amount}
        onChange={e => onUpdate(material.id, { amount: e.target.value })}
        placeholder="Amount"
        className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-stone-400"
        style={{ width: '90px' }}
      />

      <button
        onClick={() => onRemove(material.id)}
        className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

export default function Step2Materials() {
  const { state, dispatch } = usePatternStudio();
  const materials = state.pattern.materials;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = materials.findIndex(m => m.id === active.id);
    const newIdx = materials.findIndex(m => m.id === over.id);
    dispatch({ type: 'REORDER_MATERIALS', materials: arrayMove(materials, oldIdx, newIdx) });
  }

  function addBlank() {
    dispatch({ type: 'ADD_MATERIAL' });
  }

  function loadTemplate() {
    MATERIAL_TEMPLATES.forEach(t => {
      dispatch({ type: 'ADD_MATERIAL' });
    });
    // We need IDs — easier: dispatch a SET_FIELD approach with the full array
    const uid = () => crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    const mats = MATERIAL_TEMPLATES.map(t => ({ id: uid(), notes: '', ...t }));
    dispatch({ type: 'REORDER_MATERIALS', materials: mats });
  }

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold text-gray-900">Materials</h2>
        {materials.length === 0 && (
          <button
            onClick={loadTemplate}
            className="text-xs text-stone-600 underline hover:text-stone-800"
          >
            Load starter template
          </button>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-6">List everything buyers need to make this pattern.</p>

      {materials.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-stone-200 rounded-2xl bg-stone-50">
          <p className="text-sm text-gray-400 mb-4">No materials added yet.</p>
          <div className="flex gap-3">
            <button
              onClick={addBlank}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-medium transition-all active:scale-95"
              style={{ background: '#6B5744' }}
            >
              <Plus size={14} /> Add Material
            </button>
            <button
              onClick={loadTemplate}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-stone-300 text-gray-700 text-sm font-medium hover:bg-stone-50"
            >
              Load Template
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-2 hidden sm:flex items-center gap-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">
            <div className="w-4 flex-shrink-0" />
            <div style={{ width: '120px' }}>Category</div>
            <div className="flex-1">Name / Description</div>
            <div style={{ width: '90px' }}>Amount</div>
            <div className="w-8" />
          </div>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={materials.map(m => m.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {materials.map(m => (
                  <SortableRow
                    key={m.id}
                    material={m}
                    onUpdate={(id, changes) => dispatch({ type: 'UPDATE_MATERIAL', id, changes })}
                    onRemove={id => dispatch({ type: 'REMOVE_MATERIAL', id })}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <button
            onClick={addBlank}
            className="mt-4 flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-stone-400 hover:text-stone-700 transition-all w-full justify-center"
          >
            <Plus size={14} /> Add Material
          </button>
        </>
      )}

      {/* Totals summary */}
      {materials.length > 0 && (
        <div className="mt-6 p-4 bg-stone-50 rounded-xl border border-stone-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Summary</p>
          <div className="flex flex-wrap gap-3">
            {['yarn','hook','safety_eye','stuffing','notion'].map(cat => {
              const count = materials.filter(m => m.category === cat).length;
              if (!count) return null;
              const info = CATEGORIES.find(c => c.value === cat);
              return (
                <span key={cat} className="text-xs text-gray-600 bg-white px-2 py-1 rounded-lg border border-gray-200">
                  {info?.label} ×{count}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
